import _ from "lodash";
import { axiosInstance } from "./globals";
import jwtDecode from "jwt-decode";
import Cookies from "js-cookie";

import {AdminFilter, SaleAccountFilter} from "@/model/filters";
import {DataSourceRequest} from "@/model/DataSourceRequest";
import {DataSourceResult} from "@/model/DataSourceResult";
import * as util from "@/service/util";
import {DateTime} from "luxon";
import axios from "axios";
import environment from "@/environment";

export function isSignedIn(): boolean {
  return !!getToken();
}

export function getToken(): string {
  try {
    const token = Cookies.get("token") || localStorage.getItem("token");

    if (token) {
      const { exp } = jwtDecode(token) as any;

      if (DateTime.now().toUnixInteger() < exp) {
        return token;
      } else {
        signOut();
      }
    }

  } catch (e) { }
}

export function getAuthHeaders() {
  return {
    Authorization: "Bearer " + getToken()
  };
}

export function getCurrentUser() {
  if (isSignedIn()) {
    try {
      return JSON.parse(Cookies.get("user") || localStorage.getItem("user"));

    } catch (e) { }
  }
}

export function getCurrentUserId() {
  if (isSignedIn()) {
    return +(Cookies.get("userId") || localStorage.getItem("userId")) || undefined;
  }
}


export function getTokenExpTime(): number {
  if (isSignedIn()) {
    return +(Cookies.get("tokenExpire") || localStorage.getItem("tokenExpire")) || undefined;
  }
}


export async function signIn(username: string, password: string, rememberMe = false) {
  const user = (await axiosInstance.post("/Users/authenticate", { username, password })).data;
  const decodedJwt = jwtDecode(user.token) as any;

  if (rememberMe) {
    localStorage.setItem("userId", user.id);
    localStorage.setItem("token", user.token);
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("tokenExpire", decodedJwt.exp);

  } else {
    Cookies.set("userId", user.id);
    Cookies.set("token", user.token);
    Cookies.set("user", JSON.stringify(user));
    Cookies.set("tokenExpire", decodedJwt.exp);
  }

  return user;
}


export async function signInVnptSso(service: string, ticket: string) {
  const params = { service, ticket };
  const user = (await axiosInstance.post("/Users/SigninCas", null, { params })).data;
  if(user.code == 'signinCas_userNotRegistered') {
    return user;
  }
  const decodedJwt = jwtDecode(user.token) as any;

  localStorage.setItem("userId", user.id);
  localStorage.setItem("token", user.token);
  localStorage.setItem("user", JSON.stringify(user));
  localStorage.setItem("tokenExpire", decodedJwt.exp);

  localStorage.setItem("signInBySSO", "isTrue");

  return user;
}


export async function signOut() {
  try {
    await axiosInstance.post("/Users/Logout", null, { headers: getAuthHeaders() });
  } catch (error) {
  }
  Cookies.remove("userId");
  Cookies.remove("token");
  Cookies.remove("user");
  Cookies.remove("tokenExpire");

  localStorage.removeItem("userId");
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("tokenExpire");
  const signInBySSO = localStorage.getItem("signInBySSO");
  if(signInBySSO == "isTrue") {
    localStorage.removeItem("signInBySSO");
    const service = localStorage.getItem("vnptSsoService");
    const _url = 'https://id.vnpt.com.vn/cas/logout?service=' + service;
    localStorage.removeItem("vnptSsoService");
    window.location.replace(_url);
    return;
  }
  location.href = environment.appBaseUrl;
}


export async function getUser(id: number | string) {
  return (await axiosInstance.get("/Users/" + id, { headers: getAuthHeaders() })).data;
}


export async function reFetchCurrentUser() {
  const userId = getCurrentUserId();
  const user = await getUser(userId);

  if (localStorage.getItem("user")) {
    localStorage.setItem("user", JSON.stringify(user));
  } else if (Cookies.get("user")) {
    Cookies.set("user", JSON.stringify(user));
  }

  return user;
}


export async function getAdmins(filter: AdminFilter): Promise<any[]> {
  // Honestly will we even reach 500 admins? That's kinda insane and I'm lazy enough to do this
  const body: DataSourceRequest = {
    page: 1,
    pageSize: 500
  };

  if (filter.q) {
    body.filters = [
      { field: "name", operator: "contains", value: filter.q, logic: "or" },
      { field: "email", operator: "contains", value: filter.q, logic: "or" },
      { field: "phone", operator: "contains", value: filter.q, logic: "or" },
      { field: "username", operator: "contains", value: filter.q, logic: "or", filters: [] },
    ];
  }

  return (await axiosInstance.post("/Users/GetAllAdmin", body, { headers: getAuthHeaders() })).data?.data;
}


export async function createAdmin(data) {
  return (await axiosInstance.post("/Users/CreateAdmin", data, { headers: getAuthHeaders() })).data;
}


export async function createSaleAccount(data) {
  return (await axiosInstance.post("/Users/CreateManager", data, { headers: getAuthHeaders() })).data;
}


export async function getSaleAccounts(filter: SaleAccountFilter): Promise<DataSourceResult<any>> {
  const body: DataSourceRequest = {
    page: filter.page,
    pageSize: filter.pageSize,
    filters: []
  };

  const filterCount = util.countPredicate([
    filter.q, filter.areaId
  ]);

  if (filter.q && filterCount === 1) {
    body.filters = [
      { field: "name", operator: "contains", value: filter.q, logic: "or", filters: [] },
      { field: "email", operator: "contains", value: filter.q, logic: "or", filters: [] },
      { field: "phone", operator: "contains", value: filter.q, logic: "or", filters: [] },
      { field: "username", operator: "contains", value: filter.q, logic: "or", filters: [] },
    ];

  } else {
    if (filter.areaId) {
      body.filters.push({ field: "saleAreaId", operator: "eq", value: filter.areaId, logic: "and" });
    }

    if (filter.q) {
      body.filters[0].filters = [
        { field: "name", operator: "contains", value: filter.q, logic: "or" },
        { field: "email", operator: "contains", value: filter.q, logic: "or" },
        { field: "phone", operator: "contains", value: filter.q, logic: "or" },
        { field: "username", operator: "contains", value: filter.q, logic: "or", filters: [] },
      ];
    }
  }

  return (await axiosInstance.post<DataSourceResult<any>>("/Users/GetAllManager", body, { headers: getAuthHeaders() })).data;
}


export async function getCustomerAdminAccounts(filter: SaleAccountFilter): Promise<DataSourceResult<any>> {
  const body: DataSourceRequest = {
    page: filter.page,
    pageSize: filter.pageSize,
    filters: []
  };

  const filterCount = util.countPredicate([
    filter.q, filter.areaId
  ]);

  if (filter.q && filterCount === 1) {
    body.filters = [
      { field: "name", operator: "contains", value: filter.q, logic: "or", filters: [] },
      { field: "customerName", operator: "contains", value: filter.q, logic: "or", filters: [] },
      { field: "username", operator: "contains", value: filter.q, logic: "or", filters: [] },
      { field: "email", operator: "contains", value: filter.q, logic: "or", filters: [] },
      { field: "phone", operator: "contains", value: filter.q, logic: "or", filters: [] },
    ];

  } else {
    if (filter.areaId) {
      body.filters.push({ field: "saleAreaId", operator: "eq", value: filter.areaId, logic: "and" });
    }

    if (filter.q) {
      body.filters[0].filters = [
        { field: "name", operator: "contains", value: filter.q, logic: "or" },
        { field: "email", operator: "contains", value: filter.q, logic: "or" },
        { field: "phone", operator: "contains", value: filter.q, logic: "or" },
        { field: "username", operator: "contains", value: filter.q, logic: "or", filters: [] },
      ];
    }
  }

  return (await axiosInstance.post<DataSourceResult<any>>("/Users/GetAllCustomer", body, { headers: getAuthHeaders() })).data;
}


export async function updateUser(data) {
  return (await axiosInstance.post("/Users/Update", data, { headers: getAuthHeaders() })).data;
}


export async function deleteUser(id: number) {
  return (await axiosInstance.post("/Users/DeleteUser", null, {
    headers: getAuthHeaders(),
    params: { id }
  })).data;
}

export async function unlockAccount(id: number) {
  return (await axiosInstance.post("/Users/CancelLocked", null, {
    headers: getAuthHeaders(),
    params: { id }
  })).data;
}


export async function resetPassword(id: number, password: string) {
  return (await axiosInstance.post("/Users/ResetPassword", { id, password }, {
    headers: getAuthHeaders()
  })).data;
}


export async function changeMyPassword(oldPassword: string, newPassword: string) {
  return (await axiosInstance.post("/Users/ChangeMyPassword", { oldPassword, newPassword }, {
    headers: getAuthHeaders()
  })).data;
}


export async function getCustomerAdmins(customerId: number): Promise<any[]> {
  const body = { page: 1, pageSize: 500 };

  return (await axiosInstance.post<DataSourceResult<any>>(`/Users/GetAllCustomerAdmin/${customerId}`, body, {
    headers: getAuthHeaders()
  })).data.data;
}


export async function createCustomerAdmin(data) {
  const { customerId } = data;
  return (await axiosInstance.post(`/Users/CreateCustomerAdmin/${customerId}`, data, { headers: getAuthHeaders() })).data;
}


export async function getCustomerUsers(filter: AdminFilter): Promise<DataSourceResult<any>> {
  const body: DataSourceRequest = {
    page: filter.page,
    pageSize: filter.pageSize,
  };

  if (filter.q) {
    body.filters = [
      { field: "name", operator: "contains", value: filter.q, logic: "or" },
      { field: "email", operator: "contains", value: filter.q, logic: "or" },
      { field: "phone", operator: "contains", value: filter.q, logic: "or" },
    ];
  }

  return (await axiosInstance.post<DataSourceResult<any>>(`/Users/GetAllCustomerUser`, body, {
    headers: getAuthHeaders()
  })).data;
}


export async function createCustomerUser(data) {
  return (await axiosInstance.post(`/Users/CreateCustomerUser`, data, { headers: getAuthHeaders() })).data;
}

export async function forgetPassword( email: string) {
  return (await axiosInstance.post("/Users/ForgotPassword", { email })).data;
}

export async function userResetPassword(password: string, params: { secretKey: string }) {
  return (await axiosInstance.post("/Users/UserResetPassword", {password}, { params })).data;
}

export async function verifyEmail(email: string, isSecretKeyValid: boolean, secretKey: string) {
  const recaptchaToken = await util.grecaptchaExecute();
  if(isSecretKeyValid){
    return (await axiosInstance.post("/Users/VerifyEmail", { email, recaptchaToken }, {
      params: {
        secretKey: secretKey
      }
    })).data;
  }
  else{
    return (await axiosInstance.post("/Users/VerifyEmail", { email, recaptchaToken })).data;
  }
}

export async function verifyActivationCode(secretKey: string) {
  return (await axiosInstance.get("/Users/VerifyActivationCode", { params: { secretKey } })).data;
}

export async function activateAccount(data: any) {
  return (await axiosInstance.post("/Users/ActivationAccount", data, {
    params: {
      secretKey: data.secretKey
    }
  })).data;
}

export async function uploadUsersExcelFile(file: File) {
  const headers = getAuthHeaders();
  const formData = new FormData();
  formData.append("formFile", file);
  return (await axiosInstance.post("/Users/Import", formData, { headers })).data;
}

export async function getAllUserForExport() {
  const headers = getAuthHeaders();
  return (await axiosInstance.get("/Users/GetAllCustomerUser", {headers})).data;
}

export async function deleteAllViewer() {
  const headers = getAuthHeaders();
  return (await axiosInstance.post("/Users/DeleteAllCustomerUserView", null, {headers})).data;
}

